import { createEbvValueFormatter } from '@/hooks/publicSearch/ebv/ebvColumns';
import { DEFAULT_CURRENCY_SYMBOL } from '@/lib/currency';
import { displayPlainText } from '@/utils/stripHtmlForTooltip';

/**
 * Theme-aware display colour for ILR2 second-line breed averages
 * (`<font color=#800040>` / `#800000`; CANG red strain or previous-year).
 * Light: maroon, dark: lighter red — tokens in globals.css.
 */
export const RED_BREED_AVERAGE_COLOR_VAR =
  'var(--public-search-ebv-red-avg-color)';

const ILR2_RED_BREED_AVERAGE_HEX = new Set(['800040', '800000']);

function normalizeHexColor(hex: string): string {
  const raw = hex.trim().toLowerCase().replace(/^#/, '');
  if (raw.length === 3) {
    return raw
      .split('')
      .map((c) => c + c)
      .join('');
  }
  return raw.padStart(6, '0').slice(0, 6);
}

/** True for ILR2 maroon red used on dual-line breed-average values. */
export function isIlr2RedBreedAverageColor(color: string | undefined): boolean {
  if (!color) return false;
  return ILR2_RED_BREED_AVERAGE_HEX.has(normalizeHexColor(color));
}

export function isRedBreedAverageDisplayLine(
  parsedColor: string | undefined,
  lineIndex: number,
): boolean {
  return (
    isIlr2RedBreedAverageColor(parsedColor) || (lineIndex === 1 && !parsedColor)
  );
}

/** Resolved colour for rendering — uses CSS var so dark mode stays readable. */
export function resolveBreedAverageLineDisplayColor(
  parsedColor: string | undefined,
  lineIndex: number,
): string {
  if (isRedBreedAverageDisplayLine(parsedColor, lineIndex)) {
    return RED_BREED_AVERAGE_COLOR_VAR;
  }
  if (parsedColor) return parsedColor;
  return 'inherit';
}

export type BreedAverageStyledLine = {
  text: string;
  color?: string;
};

const FONT_COLOR_RE = /<font\b[^>]*\bcolor\s*=\s*#?([0-9a-fA-F]{3,6})/i;

/** True when raw ILR2 breed-average value is stacked (contains `<br>`). */
export function isDualLineBreedAverageRaw(raw: unknown): boolean {
  if (raw === null || raw === undefined) return false;
  return /<br\s*\/?>/i.test(String(raw));
}

/** Split dual-line breed-average HTML into plain values + optional per-line colour. */
export function parseBreedAverageStyledLines(
  raw: string | number | null | undefined,
): BreedAverageStyledLine[] {
  if (raw === null || raw === undefined || raw === '') return [];
  const s = String(raw).trim();
  if (!s) return [];

  if (!isDualLineBreedAverageRaw(s) && !/<[^>]+>/.test(s)) {
    return [{ text: s }];
  }

  return s
    .split(/<br\s*\/?>/gi)
    .map((part): BreedAverageStyledLine | null => {
      const colorMatch = part.match(FONT_COLOR_RE);
      const color = colorMatch ? `#${colorMatch[1]}` : undefined;
      const text = displayPlainText(part);
      if (!text) return null;
      return color ? { text, color } : { text };
    })
    .filter((line): line is BreedAverageStyledLine => line !== null);
}

/**
 * Setting 15 captions may include ILR2 markup (e.g. `<b>Black</b>`).
 * Strip all tags — emphasis is not required for Black/Red wording, and
 * this avoids `dangerouslySetInnerHTML` / attribute injection.
 */
export function sanitizeIlr2InlineCaptionHtml(raw: string): string {
  return displayPlainText(raw);
}

export function coerceBreedAverageRaw(
  value: unknown,
): string | number | null | undefined {
  if (value === null || value === undefined || value === '') {
    return value === '' ? '' : value;
  }
  if (typeof value === 'string' || typeof value === 'number') return value;
  return undefined;
}

/** ILR2 dual-line breed average: black (B) on first line, red (R) on second. */
export function buildBlackRedBreedAverageRaw(
  blackRaw: unknown,
  redRaw: unknown,
): string | null {
  const black =
    blackRaw !== null &&
    blackRaw !== undefined &&
    String(blackRaw).trim() !== ''
      ? String(blackRaw).trim()
      : null;
  const red =
    redRaw !== null && redRaw !== undefined && String(redRaw).trim() !== ''
      ? String(redRaw).trim()
      : null;
  if (black && red) {
    return `${black}<br><font color=#800040>${red}</font>`;
  }
  return black ?? red;
}

/**
 * ILR2 breed-average cells may be dual-line HTML, e.g.
 * `+3.3<br><font color=#800040>+2.2</font>` (current / previous year).
 */
export function parseBreedAverageLines(
  raw: string | number | null | undefined,
): string[] {
  if (raw === null || raw === undefined || raw === '') return [];
  const s = String(raw).trim();
  if (!s) return [];
  if (!/<[^>]+>/.test(s)) return [s];
  return s
    .split(/<br\s*\/?>/gi)
    .map((part) => displayPlainText(part))
    .filter((line) => line !== '');
}

export function formatBreedAverageLine(
  line: string,
  format: string,
  currencySymbol: string = DEFAULT_CURRENCY_SYMBOL,
): string {
  const formatter = createEbvValueFormatter(format, currencySymbol);
  const trimmed = line.trim();
  const num = parseFloat(trimmed);
  if (!isNaN(num)) return formatter(String(num));
  return trimmed;
}

/** Plain-text display for grids/tooltips — first line only when compact. */
export function formatBreedAverageDisplay(
  raw: string | number | null | undefined,
  format: string = '0',
  currencySymbol: string = DEFAULT_CURRENCY_SYMBOL,
): string {
  const lines = parseBreedAverageLines(raw);
  const firstLine = lines[0];
  if (!firstLine) return '';
  return formatBreedAverageLine(firstLine, format, currencySymbol);
}
